import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, Feather, ArrowRight, RotateCcw } from "lucide-react";

const CATS = {
  aventure: { label: "L'Aventure", icon: BookOpen, accent: "#385E3A", result: "Vous avez envie de vivre une expérience immersive.", cta: "Découvrir L'Aventure", to: "/aventures" },
  jeu: { label: "Le Jeu", icon: Sparkles, accent: "#2E5B7F", result: "Vous aimez expérimenter et découvrir en jouant.", cta: "Commencer à jouer", to: "/jeu/ile" },
  carnets: { label: "Les Carnets", icon: Feather, accent: "#A63B36", result: "Vous avez envie de prendre du temps pour vous et avancer pendant 21 jours.", cta: "Découvrir les Carnets", to: "/carnets" },
};

const QUESTIONS = [
  {
    q: "Comment avez-vous envie de découvrir le Tao des 4C ?",
    options: [
      { cat: "aventure", text: "Vivre une expérience et me laisser guider par une histoire" },
      { cat: "jeu", text: "Jouer, expérimenter et découvrir de façon ludique" },
      { cat: "carnets", text: "Prendre du temps pour moi et avancer à mon rythme" },
    ],
  },
  {
    q: "Qu'est-ce qui vous attire le plus ?",
    options: [
      { cat: "aventure", text: "Partir à la découverte de moi-même" },
      { cat: "jeu", text: "M'amuser tout en découvrant quelque chose de nouveau" },
      { cat: "carnets", text: "Comprendre, réfléchir et approfondir" },
    ],
  },
  {
    q: "Quel rythme vous correspond ?",
    options: [
      { cat: "aventure", text: "Me laisser surprendre au fil d'un parcours" },
      { cat: "jeu", text: "Des expériences courtes et interactives" },
      { cat: "carnets", text: "Un chemin de 21 jours, étape après étape" },
    ],
  },
  {
    q: "Si vous deviez commencer maintenant…",
    options: [
      { cat: "aventure", text: "Je veux entrer dans l'histoire" },
      { cat: "jeu", text: "Je veux commencer à jouer" },
      { cat: "carnets", text: "Je veux commencer mon carnet" },
    ],
  },
];

export const HomeQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const choose = (cat) => {
    const next = [...answers, cat];
    setAnswers(next);
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else setStep(QUESTIONS.length); // result
  };

  const restart = () => { setStep(0); setAnswers([]); };

  const winner = () => {
    const tally = { aventure: 0, jeu: 0, carnets: 0 };
    answers.forEach((c) => (tally[c] += 1));
    return ["aventure", "jeu", "carnets"].reduce((a, b) => (tally[b] > tally[a] ? b : a));
  };

  const finished = step >= QUESTIONS.length;

  return (
    <section className="bg-[#F6F2E9]">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 md:py-24">
        <div className="text-center">
          <p className="font-sans text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">Vous ne savez pas par où commencer ?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1F2A24] mt-2">Faites le test</h2>
          <p className="font-sans text-sm md:text-base text-[#1F2A24]/70 mt-3">Découvrez votre porte d'entrée dans le Tao des 4C.</p>
          <div className="gold-divider w-24 mt-6 mx-auto" />
        </div>

        <div className="mt-10 rounded-3xl bg-white border border-[#E8DCC4] p-6 md:p-10 shadow-sm" data-testid="home-quiz">
          {!finished ? (
            <div key={step} className="quiz-fade">
              <div className="flex items-center justify-between mb-5">
                <span data-testid="quiz-progress" className="font-sans text-xs tracking-widest text-[#1F2A24]/50">QUESTION {step + 1}/{QUESTIONS.length}</span>
                <div className="flex gap-1.5">
                  {QUESTIONS.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i <= step ? "w-6 bg-[#C9A24B]" : "w-3 bg-[#E8DCC4]"}`} />
                  ))}
                </div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-[#1F2A24] leading-snug">{QUESTIONS[step].q}</h3>
              <div className="mt-6 flex flex-col gap-3">
                {QUESTIONS[step].options.map((opt) => {
                  const Icon = CATS[opt.cat].icon;
                  return (
                    <button
                      key={opt.cat}
                      data-testid={`quiz-option-${step}-${opt.cat}`}
                      onClick={() => choose(opt.cat)}
                      className="group flex items-center gap-4 rounded-2xl border border-[#E8DCC4] bg-[#F6F2E9] px-5 py-4 text-left hover:border-[#C9A24B] hover:bg-white transition-colors"
                    >
                      <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-105" style={{ backgroundColor: CATS[opt.cat].accent }}>
                        <Icon size={18} />
                      </span>
                      <span className="font-sans text-sm md:text-base text-[#1F2A24]">{opt.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            (() => {
              const w = winner();
              const C = CATS[w];
              const Icon = C.icon;
              return (
                <div className="quiz-fade text-center" data-testid="quiz-result">
                  <span className="mx-auto w-16 h-16 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: C.accent }}>
                    <Icon size={28} />
                  </span>
                  <p className="font-sans text-xs tracking-widest text-[#1F2A24]/50 mt-5">VOTRE PORTE D'ENTRÉE</p>
                  <h3 className="font-serif text-3xl text-[#1F2A24] mt-1">{C.label}</h3>
                  <p className="font-serif italic text-lg text-[#1F2A24]/75 mt-3 max-w-md mx-auto">{C.result}</p>
                  <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to={C.to}
                      data-testid="quiz-result-cta"
                      className="inline-flex items-center gap-2 rounded-full text-white px-7 py-3.5 font-ui text-sm tracking-wide transition-colors"
                      style={{ backgroundColor: C.accent }}
                    >
                      {C.cta} <ArrowRight size={16} />
                    </Link>
                    <button
                      onClick={restart}
                      data-testid="quiz-restart"
                      className="inline-flex items-center gap-2 rounded-full border border-[#E8DCC4] text-[#1F2A24]/70 px-6 py-3.5 font-ui text-sm hover:border-[#C9A24B] hover:text-[#1F2A24] transition-colors"
                    >
                      <RotateCcw size={15} /> Refaire le test
                    </button>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>
    </section>
  );
};
